import React, { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Typography,
  FormControlLabel,
  Checkbox,
  Box,
  IconButton,
} from '@mui/material';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import { generateClient } from 'aws-amplify/api';
import { Schema } from '../../../../../amplify/data/resource';
import TextField from '../../../../components/custom/TextField';
import Select from '../../../../components/custom/Select';
import Button from '../../../../components/custom/Button';
import DatePicker from '../../../../components/custom/DatePicker';
import dayjs, { Dayjs } from 'dayjs';

const apiClient = generateClient<Schema>();

// @ts-ignore
type PaymentPlan = Schema['PaymentPlan']['type'];

interface EditPaymentPlanDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  paymentPlan: PaymentPlan | null;
}

interface Stage {
  from: number;
  to: number | null; // null означает бесконечность
  amount: string;
}

interface PaymentPlanFormData {
  title: string;
  chargeModel: 'BY_ROW' | 'BY_ID' | 'STAGES';
  chargePrice: string;
  fixedPrice: string;
  baseStageEmployees: string;
  baseStageAmount: string;
  stages: Stage[];
  discountPercent: string;
  discountExpirationDate: Dayjs | null;
  services: string[];
  active: boolean;
}

const EditPaymentPlanDialog: React.FC<EditPaymentPlanDialogProps> = ({ 
  open, 
  onClose, 
  onSubmit, 
  paymentPlan 
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<PaymentPlanFormData>({
    defaultValues: {
      title: '',
      chargeModel: 'BY_ROW',
      chargePrice: '',
      fixedPrice: '',
      baseStageEmployees: '',
      baseStageAmount: '',
      stages: [],
      discountPercent: '',
      discountExpirationDate: null,
      services: [],
      active: true,
    },
  });

  const watchedChargeModel = useWatch({
    control,
    name: 'chargeModel',
  });

  const watchedStages = useWatch({
    control,
    name: 'stages',
  });

  const watchedBaseStageEmployees = useWatch({
    control,
    name: 'baseStageEmployees',
  });

  // Автоматически обновляем первую ступень при изменении базовой ступени
  React.useEffect(() => {
    if (watchedChargeModel === 'STAGES' && watchedBaseStageEmployees) {
      const baseEmployees = parseInt(watchedBaseStageEmployees);
      if (!isNaN(baseEmployees)) {
        const currentStages = getValues('stages');
        if (currentStages.length > 0) {
          const updatedStages = [...currentStages];
          const newFrom = baseEmployees + 1;
          const currentTo = updatedStages[0].to;
          
          // Если новое from больше или равно текущему to, обновляем to
          const newTo = currentTo !== null && newFrom >= currentTo 
            ? newFrom + 10 
            : currentTo;
            
          updatedStages[0] = {
            ...updatedStages[0],
            from: newFrom,
            to: newTo
          };
          setValue('stages', updatedStages);
        }
      }
    }
  }, [watchedBaseStageEmployees, watchedChargeModel, setValue, getValues]);

  // Устанавливаем значения формы при изменении paymentPlan
  useEffect(() => {
    if (paymentPlan && open) {
      reset({
        title: paymentPlan.title || '',
        chargeModel: paymentPlan.chargeModel || 'BY_ROW',
        chargePrice: paymentPlan.chargePrice?.toString() || '',
        fixedPrice: paymentPlan.fixedPrice?.toString() || '',
        baseStageEmployees: paymentPlan.baseStageEmployees?.toString() || '',
        baseStageAmount: paymentPlan.baseStageAmount?.toString() || '',
        stages: paymentPlan.stages?.map((stage: any) => ({
          from: stage.from || 0,
          to: stage.to || null,
          amount: stage.amount?.toString() || ''
        })) || [],
        discountPercent: paymentPlan.discountPercent?.toString() || '',
        discountExpirationDate: paymentPlan.discountExpirationDate 
          ? dayjs(paymentPlan.discountExpirationDate) 
          : null,
        services: paymentPlan.services?.filter((s: any): s is string => s !== null) || [],
        active: paymentPlan.active || false,
      });
    }
  }, [paymentPlan, open, reset]);

  const chargeModelOptions = [
    { label: 'לפי שורה', value: 'BY_ROW' },
    { label: 'לפי זהות', value: 'BY_ID' },
    { label: 'מדרגות', value: 'STAGES' },
  ];

  const availableServices = [
    'MISLAKA_REPORT',
    'PAYMENT_PROCESSING',
    'FILE_MANAGEMENT',
    'DISTRIBUTION_MANAGEMENT',
  ];

  const addStage = () => {
    const currentStages = getValues('stages');
    const baseEmployees = parseInt(watchedBaseStageEmployees || '0');
    
    // Если ступеней еще нет, создаем первую начиная с baseEmployees + 1 с бесконечностью
    if (currentStages.length === 0) {
      const newStage: Stage = {
        from: baseEmployees + 1,
        to: null, // Первая ступень сразу с бесконечностью
        amount: ''
      };
      setValue('stages', [newStage]);
      return;
    }

    const lastStage = currentStages[currentStages.length - 1];
    
    // Если последняя ступень имеет to: null (бесконечность), заменяем на число
    if (lastStage.to === null) {
      const updatedLastStage = { ...lastStage, to: lastStage.from + 10 };
      const newStages = [
        ...currentStages.slice(0, -1),
        updatedLastStage,
        { from: updatedLastStage.to + 1, to: null, amount: '' }
      ];
      setValue('stages', newStages);
    } else {
      const newStage: Stage = {
        from: lastStage.to + 1,
        to: null,
        amount: ''
      };
      setValue('stages', [...currentStages, newStage]);
    }
  };

  const removeStage = (index: number) => {
    const currentStages = getValues('stages');
    if (currentStages.length <= 1) return; // Минимум одна ступень

    const newStages = currentStages.filter((_, i) => i !== index);
    
    // Убеждаемся что последняя ступень всегда имеет бесконечность
    if (newStages.length > 0) {
      newStages[newStages.length - 1].to = null;
    }
    
    // Если удаляем не последнюю ступень, пересчитываем диапазоны (кроме последней)
    if (index < newStages.length) {
      const baseEmployees = parseInt(watchedBaseStageEmployees || '0');
      const recalculatedStages = newStages.map((stage, i) => {
        if (i === 0) {
          return { ...stage, from: baseEmployees + 1 };
        } else if (i === newStages.length - 1) {
          // Последняя ступень - оставляем как есть, но убеждаемся что to = null
          const prevStage = newStages[i - 1];
          return { 
            ...stage, 
            from: prevStage.to === null ? prevStage.from + 1 : prevStage.to + 1,
            to: null
          };
        } else {
          const prevStage = newStages[i - 1];
          return { 
            ...stage, 
            from: prevStage.to === null ? prevStage.from + 1 : prevStage.to + 1 
          };
        }
      });
      setValue('stages', recalculatedStages);
    } else {
      setValue('stages', newStages);
    }
  };

  const updateStageRange = (index: number, field: 'to', value: string) => {
    const currentStages = getValues('stages');
    const newStages = [...currentStages];
    
    if (field === 'to') {
      const numValue = parseInt(value) || 0;
      
      // Не позволяем изменять последнюю ступень - она всегда должна быть бесконечной
      if (index === newStages.length - 1) {
        return;
      }
      
      newStages[index].to = numValue;
      
      // Обновляем from для следующей ступени
      if (index < newStages.length - 1) {
        newStages[index + 1].from = numValue + 1;
      }
    }
    
    setValue('stages', newStages);
  };

  const handleFormSubmit = async (data: PaymentPlanFormData) => {
    if (!paymentPlan?.id) {
      enqueueSnackbar('שגיאה: לא נמצא מזהה תוכנית התשלום', { variant: 'error' });
      return;
    }

    try {
      const submitData = {
        id: paymentPlan.id,
        title: data.title,
        chargeModel: data.chargeModel,
        chargePrice: data.chargeModel !== 'STAGES' ? parseFloat(data.chargePrice) : 0,
        fixedPrice: data.chargeModel !== 'STAGES' ? parseFloat(data.fixedPrice) : 0,
        baseStageEmployees: data.chargeModel === 'STAGES' ? parseInt(data.baseStageEmployees) : null,
        baseStageAmount: data.chargeModel === 'STAGES' ? parseFloat(data.baseStageAmount) : null,
        stages: data.chargeModel === 'STAGES' ? data.stages.map(stage => ({
          from: stage.from,
          to: stage.to || 0,
          amount: parseFloat(stage.amount)
        })) : null,
        discountPercent: data.discountPercent ? parseFloat(data.discountPercent) : 0,
        discountExpirationDate: data.discountExpirationDate 
          ? data.discountExpirationDate.toISOString() 
          : null,
        services: data.services,
        active: data.active,
        updatedAt: new Date().toISOString(),
      };

      // Обновляем план платежей в базе данных
      const result = await apiClient.models.PaymentPlan.update(submitData);
      
      if (result.data) {
        console.log('Payment plan updated successfully:', result.data);
        enqueueSnackbar('תוכנית תשלום עודכנה בהצלחה!', { variant: 'success' });
        onSubmit(result.data); // Передаем обновленный объект родителю
        onClose(); // Закрываем диалог только при успехе
      } else {
        throw new Error('Failed to update payment plan');
      }
    } catch (error) {
      console.error('Error updating payment plan:', error);
      enqueueSnackbar('שגיאה בעדכון תוכנית התשלום. אנא נסה שוב.', { variant: 'error' });
      // При ошибке диалог остается открытым и форма не сбрасывается
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: '12px',
        },
      }}
    >
      <DialogTitle sx={{ direction: 'rtl' }}>
        <Typography
          fontSize="24px"
          fontWeight="700"
          fontFamily="AlmoniDL"
          textAlign="center"
        >
          ערוך תוכנית תשלום
        </Typography>
      </DialogTitle>
      
      <DialogContent sx={{ direction: 'rtl', padding: '24px' }}>
        <Stack spacing={3}>
          <Controller
            name="title"
            control={control}
            rules={{ required: 'כותרת היא שדה חובה' }}
            render={({ field }) => (
              <TextField
                {...field}
                label="כותרת"
                error={!!errors.title}
                helperText={errors.title?.message}
                fullWidth
              />
            )}
          />

          <Controller
            name="chargeModel"
            control={control}
            rules={{ required: 'מודל חיוב הוא שדה חובה' }}
            render={({ field }) => (
              <Select
                {...field}
                label="מודל חיוב"
                options={chargeModelOptions}
                error={!!errors.chargeModel}
                helperText={errors.chargeModel?.message}
                id="chargeModel"
              />
            )}
          />

          {watchedChargeModel !== 'STAGES' && (
            <>
              <Controller
                name="chargePrice"
                control={control}
                rules={{ 
                  required: 'מחיר חיוב הוא שדה חובה',
                  pattern: {
                    value: /^\d+(\.\d+)?$/,
                    message: 'יש להזין מספר תקין'
                  }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="מחיר חיוב"
                    error={!!errors.chargePrice}
                    helperText={errors.chargePrice?.message}
                    fullWidth
                    customVariant="money"
                  />
                )}
              />

              <Controller
                name="fixedPrice"
                control={control}
                rules={{ 
                  required: 'מחיר קבוע הוא שדה חובה',
                  pattern: {
                    value: /^\d+(\.\d+)?$/,
                    message: 'יש להזין מספר תקין'
                  }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="מחיר קבוע"
                    error={!!errors.fixedPrice}
                    helperText={errors.fixedPrice?.message}
                    fullWidth
                    customVariant="money"
                  />
                )}
              />
            </>
          )}

          {watchedChargeModel === 'STAGES' && (
            <Box>
              {/* Base Stage */}
              <Typography fontFamily="AlmoniDL" fontWeight="600" fontSize="16px" mb={2}>
                מדרגה בסיס
              </Typography>
              <Box 
                sx={{ 
                  border: '2px solid #2E3A59', 
                  borderRadius: '8px', 
                  padding: '16px',
                  backgroundColor: '#F8F9FA',
                  mb: 3
                }}
              >
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Typography fontFamily="AlmoniDL" fontWeight="600" minWidth="120px">
                    מדרגת בסיס:
                  </Typography>
                  
                  <Controller
                    name="baseStageEmployees"
                    control={control}
                    rules={{ 
                      required: 'כמות עובדים היא שדה חובה',
                      pattern: {
                        value: /^\d+$/,
                        message: 'יש להזין מספר שלם'
                      }
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="כמות עובדים"
                        size="small"
                        type="number"
                        sx={{ width: '120px' }}
                        error={!!errors.baseStageEmployees}
                        helperText={errors.baseStageEmployees?.message}
                      />
                    )}
                  />
                  
                  <Controller
                    name="baseStageAmount"
                    control={control}
                    rules={{ 
                      required: 'סכום הוא שדה חובה',
                      pattern: {
                        value: /^\d+(\.\d+)?$/,
                        message: 'יש להזין מספר תקין'
                      }
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="סכום"
                        size="small"
                        customVariant="money"
                        sx={{ flexGrow: 1 }}
                        error={!!errors.baseStageAmount}
                        helperText={errors.baseStageAmount?.message}
                      />
                    )}
                  />
                </Stack>
              </Box>

              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography fontFamily="AlmoniDL" fontWeight="600" fontSize="16px">
                  מדרגות נוספות
                </Typography>
                <Button
                  onClick={addStage}
                  customVariant="light"
                  size="small"
                >
                  הוסף מדרגה
                </Button>
              </Stack>
              
              <Stack spacing={2}>
                {watchedStages?.length === 0 ? (
                  <Box sx={{ 
                    padding: '20px', 
                    textAlign: 'center', 
                    border: '1px dashed #ccc', 
                    borderRadius: '8px',
                    backgroundColor: '#FAFAFA'
                  }}>
                    <Typography fontFamily="AlmoniDL" fontSize="14px" color="#666">
                      לא הוגדרו מדרגות נוספות. לחץ על "הוסף מדרגה" להוספת מדרגה ראשונה.
                    </Typography>
                  </Box>
                ) : (
                  watchedStages?.map((stage, index) => (
                  <Box 
                    key={index} 
                    sx={{ 
                      border: '1px solid #E0E0E0', 
                      borderRadius: '8px', 
                      padding: '16px',
                      backgroundColor: '#FAFAFA'
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Typography fontFamily="AlmoniDL" fontWeight="600" minWidth="80px">
                        מדרגה {index + 1}:
                      </Typography>
                      
                      <TextField
                        label="מ-"
                        value={stage.from.toString()}
                        disabled
                        size="small"
                        sx={{ width: '80px' }}
                      />
                      
                      {stage.to === null ? (
                        <TextField
                          label="עד"
                          value="∞"
                          disabled
                          size="small"
                          sx={{ width: '80px' }}
                        />
                      ) : (
                        <TextField
                          label="עד"
                          value={stage.to.toString()}
                          onChange={(e) => updateStageRange(index, 'to', e.target.value)}
                          size="small"
                          sx={{ width: '80px' }}
                          type="number"
                        />
                      )}
                      
                      <Controller
                        name={`stages.${index}.amount`}
                        control={control}
                        rules={{ 
                          required: 'סכום הוא שדה חובה',
                          pattern: {
                            value: /^\d+(\.\d+)?$/,
                            message: 'יש להזין מספר תקין'
                          }
                        }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="סכום"
                            size="small"
                            customVariant="money"
                            sx={{ flexGrow: 1 }}
                            error={!!errors.stages?.[index]?.amount}
                            helperText={errors.stages?.[index]?.amount?.message}
                          />
                        )}
                      />
                      
                      {watchedStages.length > 1 && (
                        <IconButton 
                          onClick={() => removeStage(index)}
                          size="small"
                          sx={{ color: '#F44336' }}
                        >
                          ✕
                        </IconButton>
                      )}
                    </Stack>
                  </Box>
                  ))
                )}
              </Stack>
            </Box>
          )}

          <Controller
            name="discountPercent"
            control={control}
            rules={{ 
              pattern: {
                value: /^[0-9](\.[0-9]+)?$|^[1-9][0-9](\.[0-9]+)?$|^100$/,
                message: 'יש להזין מספר בין 0 ל-100'
              }
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label="אחוז הנחה (%) - אופציונלי"
                error={!!errors.discountPercent}
                helperText={errors.discountPercent?.message}
                fullWidth
              />
            )}
          />

          <Controller
            name="discountExpirationDate"
            control={control}
            render={({ field }) => (
              <DatePicker
                label="תאריך סיום הנחה - אופציונלי"
                value={field.value}
                onChange={field.onChange}
                error={!!errors.discountExpirationDate}
                helperText={errors.discountExpirationDate?.message}
                fullWidth
              />
            )}
          />

          <Controller
            name="active"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    {...field}
                    checked={field.value}
                    sx={{
                      color: '#2E3A59',
                      '&.Mui-checked': {
                        color: '#2E3A59',
                      },
                    }}
                  />
                }
                label={
                  <Typography fontFamily="AlmoniDL" fontWeight="600">
                    תוכנית פעילה
                  </Typography>
                }
                sx={{ direction: 'rtl' }}
              />
            )}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ direction: 'rtl', padding: '16px 24px', gap: '12px' }}>
        <Button
          onClick={handleClose}
          customVariant="light"
        >
          ביטול
        </Button>
        <Button
          onClick={handleSubmit(handleFormSubmit)}
          customVariant="default"
          loading={isSubmitting}
        >
          עדכן תוכנית
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditPaymentPlanDialog; 